/**
 * Cornerstone tool state helpers backing the "delete the annotations of this
 * image?" prompt carried by the Reset toolbar button.
 *
 * Why this does not go through the former `clearAnnotations` command: that
 * command only kept tool data carrying an `_id`, and `_id` is stamped
 * exclusively by `MeasurementApi.addMeasurement`, which is only reached through
 * `MeasurementHandlers.onAdded`. That handler had two entry points in this
 * repository: the MeasurementsPanel app extension, disabled by
 * `disableMeasurementPanel: true`, and the `onMeasurementsChanged` prop of
 * `ConnectedCornerstoneViewport`, which `react-cornerstone-viewport@4.1.1` never
 * calls. A mouse-drawn annotation therefore never received an `_id`, which made
 * `clearAnnotations` a no-op, and `MeasurementApi` never holds those
 * annotations, so there is nothing to keep in sync on that side either.
 *
 * Why the imageId is captured by the caller before the modal opens, instead of
 * calling `cornerstoneTools.clearToolState` or `removeToolState`: both of those
 * resolve the imageId from the image currently DISPLAYED on the element. The
 * user can scroll the stack while the confirmation modal is open, so they would
 * wipe the wrong slice. Reading and writing the global map through a captured
 * imageId cannot drift.
 *
 * On `saveToolState()` / `restoreToolState()`: `saveToolState()` returns the
 * live `toolState` reference, not a copy, so mutating it is already effective.
 * `dicom-rt/loadRTStruct.js:41` relies on exactly that and never calls
 * `restoreToolState`. `MeasurementApi` does save, mutate and restore
 * (`:170-174`, `:996-1006`), but it only ever `push`es and `splice`s individual
 * entries; it never deletes a whole toolType key. So neither file is a precedent
 * for the `delete toolState[imageId][toolType]` below, which is new here. The
 * `restoreToolState` call is a belt over braces: harmless, and it keeps the
 * mutation explicit should the manager ever start returning a copy.
 *
 * Everything stored for an in-scope toolType is counted, and everything counted
 * is deleted, so the prompt can never under-report what it is about to erase.
 * There used to be an `isReadOnly` filter on the count, dropped because it was
 * unreachable: `addMeasurement.js:64` is the only place in the whole source tree
 * that ever writes `isReadOnly` (verified by grep), and it always writes under
 * the `DICOMSRDisplayTool` toolType (`addMeasurement.js:25` reading
 * `SCOORD3D/constants/toolNames.js:2`). That tool extends `BaseTool`
 * (`tools/DICOMSRDisplayTool.js:37`) and so carries no `pointNearTool`, which
 * means `isAnnotationTool` already rejects the whole toolType one level up.
 * Structured report measurements are therefore never counted and never deleted,
 * and the filter could only ever have subtracted zero.
 *
 * Deliberate limits:
 * - single image scope: neighbouring slices keep their annotations, and the user
 *   gets no signal about it.
 * - no persistence: nothing is pushed back to a server or to the SR pipeline.
 */
import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';

const EMPTY_PLAN = { imageId: null, toolTypes: [] };

// Resolves the displayed imageId of an element without throwing when disabled.
const getDisplayedEnabledElement = element =>
  cornerstone
    .getEnabledElements()
    .find(candidate => candidate.element === element);

// Reads the saved tool state of a single imageId from the global manager.
const getImageToolState = imageId => {
  const toolState = cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();

  return toolState ? toolState[imageId] : null;
};

// Resolves a tool instance for an element, retrying the `Tool`-suffixed name.
const getToolInstance = (element, toolType) =>
  cornerstoneTools.getToolForElement(element, toolType) ||
  cornerstoneTools.getToolForElement(element, `${toolType}Tool`);

// True when the tool draws user annotations, ie. it extends BaseAnnotationTool.
const isAnnotationTool = (element, toolType) => {
  const tool = getToolInstance(element, toolType);

  return Boolean(tool && typeof tool.pointNearTool === 'function');
};

// Counts every item stored for a toolType, ie. exactly what a delete would drop.
const countStored = toolData => {
  const data = (toolData && toolData.data) || [];

  return data.length;
};

// True when an annotation toolType holds at least one item on the current image.
const holdsAnnotations = (element, toolType, imageToolState) =>
  isAnnotationTool(element, toolType) &&
  countStored(imageToolState[toolType]) > 0;

// Lists the toolTypes a removal would delete from the given image.
const buildPlan = (element, imageId, imageToolState) => ({
  imageId,
  toolTypes: Object.keys(imageToolState).filter(toolType =>
    holdsAnnotations(element, toolType, imageToolState)
  ),
});

// True when the plan still targets an existing entry of the tool state.
const isPlanApplicable = (plan, toolState) =>
  Boolean(
    plan &&
      plan.imageId &&
      plan.toolTypes.length &&
      toolState &&
      toolState[plan.imageId]
  );

// Resolves the imageId an element currently displays, or null.
const getDisplayedImageId = element => {
  const enabledElement = element && getDisplayedEnabledElement(element);

  return enabledElement && enabledElement.image
    ? enabledElement.image.imageId
    : null;
};

// Describes what a removal would delete on the image an element displays.
const getAnnotationPlan = element => {
  const imageId = getDisplayedImageId(element);
  const imageToolState = imageId && getImageToolState(imageId);

  return imageToolState
    ? buildPlan(element, imageId, imageToolState)
    : EMPTY_PLAN;
};

// Deletes the planned toolTypes from the captured imageId only.
const removeAnnotations = plan => {
  const manager = cornerstoneTools.globalImageIdSpecificToolStateManager;
  const toolState = manager.saveToolState();

  if (!isPlanApplicable(plan, toolState)) {
    return;
  }

  plan.toolTypes.forEach(toolType => delete toolState[plan.imageId][toolType]);
  manager.restoreToolState(toolState);
};

/**
 * Repaints a single element.
 *
 * `refreshCornerstoneViewports` of `commandsModule` would do the job, but it is
 * not exported and it redraws every enabled viewport, which is wasted work for a
 * removal scoped to one image of one viewport. The `getEnabledElements().find`
 * guard replaces `cornerstone.getEnabledElement`, which throws on a disabled
 * element instead of returning a falsy value.
 */
const refreshElement = element => {
  const enabledElement = element && getDisplayedEnabledElement(element);

  if (enabledElement && enabledElement.image) {
    cornerstone.updateImage(element);
  }
};

export { getAnnotationPlan, removeAnnotations, refreshElement };
